import * as Sentry from "@sentry/vue";
import type { App } from "vue";
import type { Router } from "vue-router";
import { InitializeSentry as PgInitializeSentry } from "@polargold/pg-frontend-core";
import { SENTRY_DATA_COLLECTION, VALID_APP_DOMAINS } from "@/utils/InitializeSentry/constants";
import { version } from "../../../package.json";

/**
 * Vue-specific glue around `pg-frontend-core`'s `InitializeSentry`: resolves the shared
 * DSN-gating/sample-rate config, then feeds it to `@sentry/vue`'s own `Sentry.init` with this
 * app's `app`/`router` instances and integrations - `pg-frontend-core`'s class deliberately
 * doesn't do this part itself, since it has no Vue Router/App coupling.
 */
export const initializeSentry = (app: App, router: Router): void => {
    const config = new PgInitializeSentry({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
        release: version,
        validAppDomains: VALID_APP_DOMAINS,
    }).resolveConfig();

    if (!config) return;

    Sentry.init({
        app,
        ...config,
        // @sentry/vue 11 changed these defaults; pinned to the v10 behaviour so upgrading
        // doesn't silently change what is collected or how sessions/traces are reported.
        attachStacktrace: false,
        traceLifecycle: "static",
        dataCollection: SENTRY_DATA_COLLECTION,
        integrations: [
            Sentry.browserTracingIntegration({ router }),
            Sentry.browserSessionIntegration({ lifecycle: "route" }),
            Sentry.replayIntegration(),
        ],
    });
};
